import { useNavigate, useParams, useLocation } from "react-router-dom";
import DetailViewLayout from "../../layouts/DetailViewLayout";
import useReport from "../../utils/hooks/reports/useReport";
import RichTextRenderer from "../../components/common/RichTextRenderer";
import ReportProgramBanner from "../../components/reports/ReportProgramBanner";
import BugInformationCard from "../../components/reports/cards/BugInformationCard";
import ProgramProfileCard from "../../components/reports/cards/ProgramProfileCard";
import useProgram from "../../utils/hooks/programs/useProgram";
import useProgramRuns from "../../utils/hooks/programs/useProgramRuns";
import usePageTitle from "../../utils/hooks/usePageTitle";
import { UserRole } from "../../utils/api/endpoints/user/credentials";
import {
  ReportState,
  ReportStatus
} from "../../utils/api/endpoints/reports/reports";
import useUserCredentials from "../../utils/hooks/user/useUserCredentials";
import OutlineButton from "../../components/buttons/OutlineButton";
import CustomActionButton from "../../components/buttons/CustomActionButton";
import InfoCardContainer from "../../components/common/InfoCardContainer";
import { ReportRejectReason } from "../../utils/api/endpoints/reports/parseReports";
import InlineContainer from "../../components/common/InlineContainer";
import DeleteIcon from "../../assets/icons/DeleteIcon";
import EditIcon from "../../assets/icons/EditIcon";
import { RewardTiers } from "../../components/editor/inputs/programs/ProgramRewardsInput";
import toast from "react-hot-toast";
import { TriageStatus } from "../../utils/api/endpoints/usersApi";
import DownloadIcon from "../../assets/icons/DownloadIcon";
import { ProgramType } from "../../utils/api/endpoints/programs/parsePrograms";
import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { AxiosError } from "axios";
import DocumentationLink from "../../components/reports/cards/DocumentationLink";
import CommentBox from "../../components/forms/inputs/CommentBox";
// import AutoAcceptCountdown from "../../components/reports/AutoAcceptCountdown";
import {
  createReportComment,
  deleteReportComment,
  getReportComments,
  editReportComment
} from "../../utils/api/endpoints/reportCommentsApi";
import {
  useMarkReportAsReadMutation,
  useBulkArchiveReportsMutation
} from "../../utils/api/endpoints/reportsApi";
import { useDispatch } from "react-redux";
import { FaArchive, FaBoxOpen } from "react-icons/fa";
import { api } from "../../utils/api/api";
import {
  deleteRetestRequest,
  getRetestStatus,
  retestRequest
} from "../../utils/api/endpoints/retests/retests";
import ConfirmationModal from "../../components/common/ConfirmationModal";
import { REPORT_STATUS_INFO } from "../../utils/constants/reportStatusInfo";
import { ReportRecommendation } from "../../utils/api/endpoints/reports/parseReports";
import ByReportIcon from "../../assets/icons/by-report.svg";
import FromReportIcon from "../../assets/icons/from-report.svg";
import WithReportIcon from "../../assets/icons/with-report.svg";
import TargetingReportIcon from "../../assets/icons/targeting-report.svg";
import ProgramIcon from "../../assets/icons/program-icon.svg";
import { parseCategoryString } from "../../utils/categoryUtils";
import ReportStatusPill from "../../components/reports/ReportStatusPill";
import { getSeverityColourWithBg } from "../../components/reports/VulnerabilityFlag";
import { useCreateManualDelegationMutation, useGetObjectDelegationsStatusQuery } from "../../utils/api/endpoints/delegationsApi";
import usePrivateAccessUsers from "../../utils/hooks/user/usePrivateAccessUsers";
import ScopeDisplay from "../../components/common/ScopeDisplay";

const ReportPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { role, id: userId, username } = useUserCredentials();
  const [createManualDelegation] = useCreateManualDelegationMutation();
  const isAdminGroup = [UserRole.ADMIN, UserRole.SUB_ADMIN, UserRole.ADMIN_MANAGER, UserRole.QA].includes(role as UserRole);

  const dispatch = useDispatch();
  const [currentRetestStatus, setCurrentRetestStatus] =
    useState<string>("Request Retest");
  const [retestRequested, setRetestRequested] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const isBusinessRole = useRef<boolean>(false);
  const [activeSection, setActiveSection] = useState<string | null>(
    "Vulnerability Details"
  );

  const [comments, setComments] = useState<any[]>([]);
  const [modalConfig, setModalConfig] = useState<{
    isOpen: boolean;
    title: string;
    description: string;
    onConfirm: () => void;
    info?: string;
  }>({
    isOpen: false,
    title: "",
    description: "",
    onConfirm: () => { }
  });

  const {
    report,
    updateReportState,
    generateCertificate,
    viewCertificate,
    initiatePayment,
    deleteReport,
    submitReport,
    recommendations,
    isLoadingRecommendations
  } = useReport(id !== undefined ? parseInt(id) : undefined);
  const { program } = useProgram(report?.programId);
  const { runs } = useProgramRuns(report?.programId);
  const reportRunTitle = report?.runId ? runs?.find((r: any) => r.run_id === report.runId)?.run_title : undefined;
  const isContinuousProgram = (program as any)?.scheduling_type === 'continuous' || (program as any)?.schedulingType === 'continuous';
  // Eligible researchers for manual delegation (program private access users) - only for admin
  const { privateAccessUsers } = usePrivateAccessUsers(isAdminGroup ? program?.id : undefined);
  const [delegatePickerOpen, setDelegatePickerOpen] = useState(false);
  const [isScopeExpanded, setIsScopeExpanded] = useState(false);
  const [needsReadMore, setNeedsReadMore] = useState(false);
  const scopeRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (scopeRef.current) {
      // Check if the scroll height exceeds a certain threshold (match maxHeight)
      setNeedsReadMore(scopeRef.current.scrollHeight > 250);
    }
  }, [report?.scope]);

  // Delegation status for this report's comments (admin-only)
  const { data: commentDelegations, refetch: refetchCommentDelegations } = useGetObjectDelegationsStatusQuery(
    { scope: "report_comment", report_id: id ? parseInt(id) : undefined },
    { skip: !isAdminGroup || !id }
  );

  const handleSelectCommentDelegate = async (to_user_id: number) => {
    if (!id) return;
    try {
      await (createManualDelegation({ scope: "report_comment", report_id: parseInt(id), to_user_id }) as any).unwrap();
      toast.success("Delegated comments access");
      setDelegatePickerOpen(false);
      refetchCommentDelegations();
    } catch {
      toast.error("Failed to delegate comments access");
    }
  };


  // Hook to mark report as read
  const [markReportAsRead] = useMarkReportAsReadMutation();
  const [bulkArchiveReports] = useBulkArchiveReportsMutation();

  const handleArchiveStatus = async (archive: boolean) => {
    if (!id) return;
    try {
      await bulkArchiveReports({ reportIds: [parseInt(id)], archive }).unwrap();
      toast.success(`Report successfully ${archive ? 'archived' : 'unarchived'}`);
      dispatch(api.util.invalidateTags([{ type: 'Report', id: parseInt(id) }]));
    } catch {
      toast.error(`Failed to ${archive ? 'archive' : 'unarchive'} report.`);
    }
  };

  const location = useLocation();
  const prevPathRef = useRef(location.pathname);

  usePageTitle(report?.title || "Report");

  // Store the current report ID and page number when viewing
  // Store the current report ID when viewing
  useEffect(() => {
    if (id) {
      localStorage.setItem("lastViewedReportId", id);
    }
  }, [id]);

  // Scroll to comments section if hash is present
  useEffect(() => {
    if (location.hash === "#comments-section" || location.hash === "#comments") {
      // Delay slightly to allow comments to load and render
      setTimeout(() => {
        const element = document.getElementById("comments-section");
        if (element) {
          element.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }, 300);
    }
  }, [location.hash, comments]);

  // Mark report as read when business user opens it
  useEffect(() => {
    const markAsRead = async () => {
      if (id && isBusinessRole.current && report && !report.read) {
        try {
          await markReportAsRead(parseInt(id)).unwrap();
        } catch (error) {
          console.error("Failed to mark report as read:", error);
        }
      }
    };

    markAsRead();
  }, [id, isBusinessRole.current, report, markReportAsRead]);

  // Get the return link with the correct page number
  const returnLink = useMemo(() => {
    // Prefer the page number from the URL, fall back to localStorage, then default to 1
    const urlPage = new URLSearchParams(location.search).get("page");
    const storedPage = localStorage.getItem("reportsCurrentPage");
    const page = urlPage || storedPage || "1";
    return `/dashboard/reports?page=${page}`;
  }, [location.search]);

  // Clear the last viewed report ID and page number when navigating away from reports
  useEffect(() => {
    const isReportsPath = location.pathname.startsWith("/dashboard/reports");
    const isReportDetailPath = /^\/dashboard\/reports\/\d+$/.test(
      location.pathname
    );

    if (!isReportsPath && !isReportDetailPath) {
      localStorage.removeItem("lastViewedReportId");
      localStorage.removeItem("reportsCurrentPage");
    }
    prevPathRef.current = location.pathname;
  }, [location.pathname]);

  // Update the ref when the role changes
  useEffect(() => {
    isBusinessRole.current = role
      ? [
        UserRole.BUSINESS,
        UserRole.BUSINESS_MANAGER,
        UserRole.DEVELOPER,
        UserRole.BUSINESS_ADMINISTRATOR
      ].includes(role)
      : false;
  }, [role]);

  // Fetch retest status if the role is BUSINESS
  useEffect(() => {
    const fetchRetestStatus = async () => {
      if (id && isBusinessRole.current) {
        try {
          const status = await getRetestStatus(parseInt(id));
          setCurrentRetestStatus(status || "Request Retest");
          setRetestRequested(status === "Retest Requested");
        } catch (err) {
          if ((err as AxiosError).response?.status === 404) {
            // Handle 404 error by setting default status
            setCurrentRetestStatus("Request Retest");
            setRetestRequested(false);
          } else {
            toast.error("Failed to fetch retest status. Please try again.");
          }
        }
      }
    };
    fetchRetestStatus();
  }, [id]);

  const handleInitiatePayment = useCallback(() => {
    if (report && report.severity && program && program.payoutRange) {
      const severity =
        report.severity.category.toLowerCase() as keyof RewardTiers;
      const amount = program.payoutRange[severity];

      if (amount) {
        initiatePayment(amount);
      } else {
        toast.error(
          "Unable to initiate payment. Missing payout range in program."
        );
      }
    } else {
      toast.error(
        "Report or program is missing essential details - please contact an admin"
      );
    }
  }, [report, program, initiatePayment]);

  const handleRetestRequest = useCallback(async () => {
    if (id) {
      try {
        setCurrentRetestStatus("Retest Requested");
        setRetestRequested(true);
        await retestRequest(parseInt(id), undefined, report?.runId);
        toast.success("Retest request submitted successfully.");

        // Invalidate the report query to refetch updated data with retest status
        dispatch(api.util.invalidateTags([{ type: 'Report', id: parseInt(id) }]));
      } catch (err) {
        toast.error("Failed to submit retest request. Please try again.");
      }
    }
  }, [id, dispatch]);

  const handleDeleteRetestRequest = useCallback(async () => {
    if (id) {
      try {
        setCurrentRetestStatus("Request Retest");
        setRetestRequested(false);
        await deleteRetestRequest(id);
        toast.success("Retest request deleted successfully.");

        // Invalidate the report query to refetch updated data without retest status
        dispatch(api.util.invalidateTags([{ type: 'Report', id: parseInt(id) }]));
      } catch (err) {
        toast.error("Failed to delete retest request. Please try again.");
      }
    }
  }, [id, dispatch]);

  const sections = useMemo(
    () =>
      [
        { title: "Vulnerability Details", content: report?.description },
        { title: "Validation Steps", content: report?.instructions },
        { title: "Impact", content: report?.impact },
        { title: "Recommended Fix", content: report?.fix },
        { title: "Additional Information", content: report?.additionalInfo }
      ].filter(section => section.content),
    [report]
  );

  const handleSectionChange = useCallback((sectionTitle: string) => {
    setActiveSection(sectionTitle);
  }, []);

  // Fetch comments
  const fetchComments = useCallback(async () => {
    if (id) {
      const data = await getReportComments(parseInt(id));
      setComments(data || []);
    }
  }, [id]);

  useEffect(() => {
    fetchComments();
  }, [fetchComments]);

  const addNewComment = useCallback(
    async (reportId: number, userComment: string) => {
      await createReportComment(reportId, userComment);
      // Refresh comments after adding
      await fetchComments();
    },
    [fetchComments]
  );

  const deleteComment = useCallback(async (commentId: number) => {
    await deleteReportComment(commentId);
    // Refresh comments after deleting
    await fetchComments();
  }, [fetchComments]);

  const editCurrentComment = useCallback(
    async (commentId: number, comment: string) => {
      await editReportComment(commentId, comment);
      // Refresh comments after editing
      await fetchComments();
    },
    [fetchComments]
  );

  const handleSubmitReport = useCallback(async () => {
    if (report?.id && !isSubmitting) {
      setIsSubmitting(true);
      try {
        await submitReport(report.id);
        toast.success("Report submitted successfully");
      } catch (error) {
        toast.error("Failed to submit report. Please try again.");
      } finally {
        setIsSubmitting(false);
      }
    }
  }, [report?.id, submitReport, isSubmitting]);

  // Helper function to show confirmation modal
  const showConfirmation = (
    title: string,
    description: string,
    onConfirm: () => void,
    info?: string
  ) => {
    setModalConfig({
      isOpen: true,
      title,
      description,
      onConfirm,
      info
    });
  };

  // Helper function to close modal
  const closeModal = () => {
    setModalConfig(prev => ({ ...prev, isOpen: false }));
  };

  return (
    <section className="montserrat-font flex h-full flex-col justify-between">
      <DetailViewLayout
        link={returnLink}
        label="Return to Reports"
        className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_360px]"
      >
        {report && (
          <>
            {/* Left Column - 2 Containers */}
            <section className="flex flex-col gap-6">
              {/* Container 1: Combined Dark Blue Header + White Tabs Card */}
              <div className="rounded-xl border border-gray-200 bg-white shadow-md overflow-hidden">
                {/* Dark Blue Header Section */}
                {program && (
                  <div className="p-8 text-white" style={{ backgroundColor: '#010D2C' }}>
                    <h1 className="text-xl font-semibold mb-8">
                      <RichTextRenderer content={report.title} />
                    </h1>
                    <div className="grid grid-cols-2 gap-x-12 gap-y-6 items-start">
                      <div className="flex items-center gap-4">
                        <img src={ByReportIcon} alt="By" className="w-8 h-8" />
                        <div>
                          <span className="text-white text-base font-normal">By: </span>
                          <button
                            onClick={() => {
                              if (role === UserRole.RESEARCHER) {
                                navigate(`/dashboard/profile`);
                                return;
                              }
                              // Admin roles use userId-based route, business roles use username-based route
                              const isAdminGroup = [UserRole.ADMIN, UserRole.SUB_ADMIN, UserRole.ADMIN_MANAGER, UserRole.QA].includes(role as UserRole);
                              if (isAdminGroup) {
                                const researcherId = report.creator?.id || report.userId;
                                if (researcherId) navigate(`/dashboard/users/researcher-profile/${researcherId}`);
                              } else {
                                const username = report.creator?.username;
                                if (username) navigate(`/dashboard/users/researcher-profile/${username}`);
                              }
                            }}
                            className="text-base font-medium hover:underline cursor-pointer"
                            style={{ color: '#58CC02' }}
                          >
                            {report.creator?.displayName || "Unknown"}
                          </button>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <img src={WithReportIcon} alt="With" className="w-8 h-8" />
                        <div>
                          <span className="text-white text-base font-normal">With: </span>
                          <span className="text-base font-medium" style={{ color: '#58CC02' }}>{program.creator?.displayName || program.creator?.username || "Capture The Bug"}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <img src={FromReportIcon} alt="From" className="w-8 h-8" />
                        <div>
                          <span className="text-white text-base font-normal">From: </span>
                          <span
                            className="text-base font-medium cursor-pointer hover:underline"
                            style={{ color: '#58CC02' }}
                            onClick={() => program && navigate(`/dashboard/programs/${program.id}`)}
                          >
                            {program?.title || "Unknown Program"}
                            {isContinuousProgram && reportRunTitle && (
                              <span className="text-white font-normal"> | {reportRunTitle}</span>
                            )}
                          </span>
                        </div>
                      </div>
                      {(report.scope || report.assetName || (report.targetValues && report.targetValues.length > 0)) && (
                        <ScopeDisplay
                          scope={report.scope}
                          assetName={report.assetName}
                          targetValues={report.targetValues}
                          endpointValues={report.endpointValues}
                          targetEndpointMap={report.targetEndpointMap}
                          targetMetadataMap={report.targetMetadata}
                          programAssetName={program?.asset_name}
                          programTitle={program?.title}
                          isV1={(program?.architecture_version ?? 0) < 2}
                        />
                      )}
                    </div>
                  </div>
                )}

                {/* White Tabs and Content Section */}
                {sections.length > 0 && (
                  <>
                    {/* Tabs */}
                    <div className="flex border-b border-gray-200 px-8">
                      {sections.map(section => (
                        <button
                          key={section.title}
                          className={`px-8 py-6 text-sm font-medium ${activeSection === section.title
                            ? "border-b-2 border-green-500"
                            : "text-gray-500 hover:text-gray-700 hover:bg-gray-50"
                            }`}
                          style={{
                            color: activeSection === section.title ? '#2563EB' : undefined
                          }}
                          onClick={() => handleSectionChange(section.title)}
                        >
                          {section.title}
                        </button>
                      ))}
                    </div>

                    {/* Content */}
                    {sections.map(section => (
                      <div
                        key={section.title}
                        className={`${activeSection === section.title ? "block" : "hidden"
                          }`}
                      >
                        {section.content && (
                          <div className="px-8 py-8">
                            {section.title === "Validation Steps" && (
                              <h3 className="text-xl font-bold text-gray-900 mb-6">Steps to reproduce:</h3>
                            )}
                            <div className="font-normal text-gray-800 text-base leading-relaxed">
                              <RichTextRenderer content={section.content} />
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </>
                )}
              </div>

              {/* Container 2: White Card with Comments */}
              <div id="comments-section" className="rounded-xl border border-gray-200 bg-white p-6 shadow-md">
                <div className="mb-4 flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900">Comments</h3>
                  {isAdminGroup && (
                    <div className="relative">
                      <OutlineButton onClick={() => setDelegatePickerOpen(v => !v)} className="py-1 px-3">
                        Delegate comments
                      </OutlineButton>
                      {delegatePickerOpen && (
                        <div className="absolute right-0 z-10 mt-2 max-h-64 w-72 overflow-y-auto rounded-md border border-gray-200 bg-white shadow-lg">
                          {(() => {
                            // Get list of already delegated users (with accepted status)
                            const delegatedUserIds = (commentDelegations?.data || [])
                              .filter((d: any) => d.status === "accepted")
                              .map((d: any) => d.to_user_id);

                            // Get report owner's user ID
                            const reportOwnerId = report?.userId;

                            // Filter researchers: exclude owner and already delegated
                            const eligibleResearchers = (privateAccessUsers || []).filter((u: any) =>
                              u.id !== reportOwnerId && !delegatedUserIds.includes(u.id)
                            );

                            return eligibleResearchers.length > 0 ? (
                              eligibleResearchers.map((u: any) => (
                                <button
                                  key={u.id}
                                  onClick={() => handleSelectCommentDelegate(u.id)}
                                  className="flex w-full items-center gap-2 p-2 text-left hover:bg-gray-50"
                                >
                                  {u.pfp && <img src={u.pfp} alt="" className="h-6 w-6 rounded-full" />}
                                  <div className="flex flex-col">
                                    <span className="text-sm font-medium">{u.displayName || u.username}</span>
                                    {u.username && (
                                      <span className="text-xs text-gray-500">@{u.username}</span>
                                    )}
                                  </div>
                                </button>
                              ))
                            ) : (
                              <div className="p-3 text-sm text-gray-500">No eligible researchers</div>
                            );
                          })()}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {isAdminGroup && (
                  <div className="mb-3 rounded-md border border-gray-100 bg-gray-50 p-2 text-sm">
                    {(() => {
                      const list = commentDelegations?.data || [];
                      if (list.length === 0) return <span className="text-gray-500">No delegations yet</span>;
                      const latest = list[0];
                      const delegate = privateAccessUsers?.find((u: any) => u.id === latest.to_user_id);
                      const statusColor =
                        latest.status === "accepted"
                          ? "bg-green-100 text-green-800"
                          : latest.status === "pending"
                            ? "bg-yellow-100 text-yellow-800"
                            : latest.status === "rejected"
                              ? "bg-red-100 text-red-800"
                              : "bg-gray-100 text-gray-800";
                      return (
                        <div className="flex flex-wrap items-center gap-2">
                          <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${statusColor}`}>
                            {latest.status.toUpperCase()}
                          </span>
                          {delegate && (
                            <span>
                              Comments delegated to {delegate.displayName || `@${(delegate as any).username}`}
                            </span>
                          )}
                          {latest.expires_at && latest.status === "pending" && (
                            <span className="text-gray-500">
                              Expires {new Date(latest.expires_at).toLocaleString()}
                            </span>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                )}

                <div className="border-t mb-4 -mx-6" style={{ borderColor: '#E5E7EB' }}></div>
                <CommentBox
                  commentList={comments}
                  reportId={report?.id}
                  userId={userId}
                  username={username}
                  currentUserRole={role}
                  onSubmitComment={addNewComment}
                  onDeleteComment={deleteComment}
                  onEditComment={editCurrentComment}
                />
              </div>
            </section>

            {/* Container 3: Top Right - Program Information */}
            <section className="flex flex-col">
              <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-md mb-6">
                {/* Program Header */}
                <div className="mb-6">
                  <h2 className="text-lg font-semibold text-blue-700 mb-3">{program?.title || "Vulnerability Disclosure Program"}</h2>
                  <div className="flex items-center gap-2 mb-4">
                    <img src={ProgramIcon} alt="Program" className="w-10 h-10" />
                    <div>
                      <div className="font-medium text-gray-900">{program?.creator?.displayName || program?.creator?.username || "Capture The Bug"}</div>
                      <div className="text-gray-500 text-sm">{program?.createdAt ? new Date(program.createdAt).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : "February 13, 2024"}</div>
                    </div>
                  </div>
                </div>
                <div className="border-t border-gray-200 mb-6 -mx-6"></div>

                {/* Report Status and Submission Date */}
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm font-medium text-gray-700">Report Status</span>
                    {report && (
                      <div className="flex items-center gap-2">
                        <ReportStatusPill
                          report={report}
                          showRetestStatus={true}
                          className="px-2 py-1 text-xs font-medium"
                        />
                        {report.isArchived && (
                          <span className="inline-flex items-center rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-bold tracking-wider text-gray-600 border border-gray-200 uppercase shadow-sm">
                            Archived
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                <div className="border-t border-gray-200 mb-6 -mx-6"></div>

                <div className="mb-6">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-700">Submitted On :</span>
                    <span className="text-sm text-gray-600">{report.submittedDate || "November 27, 2024"}</span>
                  </div>
                </div>
                <div className="border-t border-gray-200 mb-6 -mx-6"></div>

                {/* Bug Information Section */}
                <div className="mb-6">
                  <h3 className="text-base font-semibold text-blue-700 mb-3">Bug Information</h3>
                  <div className="space-y-3">
                    {report.category && (
                      <div className="flex items-start">
                        <span className="text-sm font-medium text-gray-700 flex-shrink-0" style={{ width: '140px' }}>Category:</span>
                        <div className="text-xs text-blue-600 ml-3">
                          {parseCategoryString(report.category)}
                        </div>
                      </div>
                    )}
                    <div className="flex items-center">
                      <span className="text-sm font-medium text-gray-700 flex-shrink-0" style={{ width: '140px' }}>Severity Score:</span>
                      <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ml-3 ${report.severity ? getSeverityColourWithBg(report.severity) : 'text-gray-600 bg-gray-100'}`}>
                        {report.severity?.score || "5.3"}
                      </span>
                    </div>
                    <div className="flex items-center">
                      <span className="text-sm font-medium text-gray-700 flex-shrink-0" style={{ width: '140px' }}>Severity Level:</span>
                      <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ml-3 ${report.severity ? getSeverityColourWithBg(report.severity) : 'text-gray-600 bg-gray-100'}`}>
                        {report.severity?.category?.toUpperCase() === "NONE" ? "Info" : (report.severity?.category || "High")}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Researcher/Admin toolbar */}
                {((role === UserRole.RESEARCHER &&
                  (report.status === ReportStatus.DRAFT ||
                    report.state === ReportState.UNDER_REVIEW ||
                    report.state === ReportState.TRIAGE_REJECTED ||
                    report.state === ReportState.BUSINESS_REJECTED)) ||
                  role === UserRole.ADMIN ||
                  role === UserRole.SUB_ADMIN) && (
                    <div className="mb-4 flex gap-2">
                      <CustomActionButton
                        onClick={() => navigate("edit")}
                        className={`gap-2 ${(role === UserRole.RESEARCHER &&
                          (report.status === ReportStatus.DRAFT ||
                            report.state === ReportState.UNDER_REVIEW)) ? 'flex-1' : 'w-full'}`}
                      >
                        <EditIcon />
                        Edit
                      </CustomActionButton>
                      {(role === UserRole.RESEARCHER &&
                        (report.status === ReportStatus.DRAFT ||
                          report.state === ReportState.UNDER_REVIEW)) && (
                          <CustomActionButton
                            onClick={deleteReport}
                            className="gap-2 flex-1"
                          >
                            <DeleteIcon />
                            Delete
                          </CustomActionButton>
                        )}
                    </div>
                  )}

                {role === UserRole.QA &&
                  (report.status === ReportStatus.DRAFT ||
                    report.state === ReportState.UNDER_REVIEW) && (
                    <div className="mb-4 flex gap-2">
                      <CustomActionButton
                        onClick={() => navigate("edit")}
                        className="gap-2 w-full"
                      >
                        <EditIcon />
                        Edit
                      </CustomActionButton>
                    </div>
                  )}
                {/* Researcher toolbar for BUSINESS_REQUEST_INFO state */}
                {role === UserRole.RESEARCHER &&
                  report.state === ReportState.BUSINESS_REQUEST_INFO && (
                    <div className="mb-4 flex gap-2">
                      <CustomActionButton
                        onClick={() => navigate("edit")}
                        className="gap-2 w-full"
                      >
                        <EditIcon />
                        Edit
                      </CustomActionButton>
                    </div>
                  )}

                {/* Action Buttons */}
                <div className="flex flex-col gap-3">
                  {(((role === UserRole.ADMIN ||
                    role === UserRole.SUB_ADMIN ||
                    role === UserRole.ADMIN_MANAGER ||
                    role === UserRole.QA) &&
                    (report.triageStatus === TriageStatus.UNDER_REVIEW ||
                      report.state === ReportState.TRIAGE_REJECTED)) ||
                    ((role === UserRole.BUSINESS ||
                      role === UserRole.DEVELOPER ||
                      role === UserRole.BUSINESS_MANAGER ||
                      role === UserRole.BUSINESS_ADMINISTRATOR) &&
                      !(program?.type !== ProgramType.PTAAS) &&
                      report.state === ReportState.TRIAGE_APPROVED)) && (
                      <CustomActionButton
                        onClick={() =>
                          showConfirmation(
                            REPORT_STATUS_INFO.APPROVE.title,
                            REPORT_STATUS_INFO.APPROVE.description,
                            () => updateReportState("approved"),
                            REPORT_STATUS_INFO.APPROVE.info
                          )
                        }
                      >
                        Approve
                      </CustomActionButton>
                    )}

                  {(role === UserRole.BUSINESS ||
                    role === UserRole.BUSINESS_MANAGER ||
                    role === UserRole.BUSINESS_ADMINISTRATOR ||
                    role === UserRole.ADMIN ||
                    role === UserRole.SUB_ADMIN ||
                    role === UserRole.ADMIN_MANAGER) && (
                      <CustomActionButton
                        className="bg-gray-100 hover:bg-gray-200 text-gray-800 border-gray-300"
                        onClick={() =>
                          showConfirmation(
                            report.isArchived ? "Unarchive Report" : "Archive Report",
                            `Are you sure you want to ${report.isArchived ? "unarchive" : "archive"} this report?`,
                            () => handleArchiveStatus(!report.isArchived)
                          )
                        }
                      >
                        {report.isArchived ? (
                          <>
                            <FaBoxOpen className="mr-2 h-4 w-4" />
                            Unarchive
                          </>
                        ) : (
                          <>
                            <FaArchive className="mr-2 h-4 w-4" />
                            Archive
                          </>
                        )}
                      </CustomActionButton>
                    )}

                  {(role === UserRole.BUSINESS ||
                    role === UserRole.DEVELOPER ||
                    role === UserRole.BUSINESS_MANAGER ||
                    role === UserRole.BUSINESS_ADMINISTRATOR) &&
                    report.state !== ReportState.CLOSED &&
                    (report.state === ReportState.TRIAGE_APPROVED ||
                      report.state === ReportState.FIX_APPROVED ||
                      report.state === ReportState.FIX_VERIFIED ||
                      currentRetestStatus === ReportState.FIX_VERIFIED ||
                      currentRetestStatus === ReportState.FIX_APPROVED ||
                      currentRetestStatus === ReportState.CLOSE_RETEST) && (
                      <CustomActionButton
                        onClick={() =>
                          showConfirmation(
                            REPORT_STATUS_INFO.CLOSE.title,
                            REPORT_STATUS_INFO.CLOSE.description,
                            () =>
                              updateReportState(
                                "closed",
                                ReportRejectReason.INFORMATIVE
                              ),
                            REPORT_STATUS_INFO.CLOSE.info
                          )
                        }
                      >
                        Close
                      </CustomActionButton>
                    )}

                  {(role === UserRole.ADMIN ||
                    role === UserRole.SUB_ADMIN ||
                    role === UserRole.ADMIN_MANAGER ||
                    role === UserRole.QA) &&
                    (report.triageStatus === TriageStatus.APPROVED ||
                      report.triageStatus === TriageStatus.UNDER_REVIEW ||
                      report.state === ReportState.TRIAGE_REJECTED ||
                      report.state === ReportState.TRIAGE_APPROVED) && (
                      <CustomActionButton
                        onClick={() =>
                          showConfirmation(
                            REPORT_STATUS_INFO.REJECT.title,
                            REPORT_STATUS_INFO.REJECT.description,
                            () =>
                              updateReportState(
                                "rejected",
                                ReportRejectReason.INFORMATIVE
                              ),
                            REPORT_STATUS_INFO.REJECT.info
                          )
                        }
                      >
                        Reject
                      </CustomActionButton>
                    )}

                  {(role === UserRole.ADMIN ||
                    role === UserRole.SUB_ADMIN ||
                    role === UserRole.ADMIN_MANAGER ||
                    role === UserRole.QA) &&
                    (report.state === ReportState.BUSINESS_APPROVED ||
                      report.state === ReportState.PAYMENT_INITIATED ||
                      report.state === ReportState.CLOSED) &&
                    !report.certificateGenerated && (
                      <CustomActionButton
                        onClick={() =>
                          showConfirmation(
                            REPORT_STATUS_INFO.GENERATE_CERTIFICATE.title,
                            REPORT_STATUS_INFO.GENERATE_CERTIFICATE.description,
                            generateCertificate,
                            REPORT_STATUS_INFO.GENERATE_CERTIFICATE.info
                          )
                        }
                      >
                        Generate Certificate
                      </CustomActionButton>
                    )}

                  {(role === UserRole.ADMIN || role === UserRole.RESEARCHER) &&
                    report.certificateGenerated && (
                      <CustomActionButton onClick={viewCertificate}>
                        View Certificate
                      </CustomActionButton>
                    )}

                  {!!(
                    role === UserRole.BUSINESS ||
                    role === UserRole.DEVELOPER ||
                    role === UserRole.BUSINESS_MANAGER ||
                    role === UserRole.BUSINESS_ADMINISTRATOR
                  ) &&
                    !!(program?.type !== ProgramType.PTAAS) &&
                    report.state === ReportState.BUSINESS_APPROVED && (
                      <CustomActionButton
                        onClick={() =>
                          showConfirmation(
                            REPORT_STATUS_INFO.INITIATE_PAYMENT.title,
                            REPORT_STATUS_INFO.INITIATE_PAYMENT.description,
                            handleInitiatePayment,
                            REPORT_STATUS_INFO.INITIATE_PAYMENT.info
                          )
                        }
                      >
                        Initiate Payment
                      </CustomActionButton>
                    )}

                  {!!(
                    role === UserRole.BUSINESS ||
                    role === UserRole.DEVELOPER ||
                    role === UserRole.BUSINESS_MANAGER ||
                    role === UserRole.BUSINESS_ADMINISTRATOR ||
                    isAdminGroup
                  ) &&
                    (report.state === ReportState.BUSINESS_ACCEPT_RISK ||
                      report.state === ReportState.BUSINESS_NON_ACTIONABLE_ISSUE) && (
                      <div className="flex flex-col gap-2 border-t border-gray-100 pt-4 mt-2">
                        <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest px-1">
                          Undo your current status
                        </span>
                        <CustomActionButton
                          onClick={() =>
                            showConfirmation(
                              REPORT_STATUS_INFO.UNDO.title,
                              REPORT_STATUS_INFO.UNDO.description,
                              () => updateReportState("undo"),
                              REPORT_STATUS_INFO.UNDO.info
                            )
                          }
                          className="!bg-white !text-red-500 border border-red-200 hover:!bg-red-50 transition-all font-semibold shadow-sm"
                        >
                          Undo Status
                        </CustomActionButton>
                      </div>
                    )}

                  {(role === UserRole.BUSINESS ||
                    role === UserRole.DEVELOPER ||
                    role === UserRole.BUSINESS_MANAGER ||
                    role === UserRole.BUSINESS_ADMINISTRATOR) &&
                    (report.state === ReportState.AWAITING_FIX ||
                      report.state === ReportState.RETEST_REQUESTED ||
                      (report.state as any) === "retest_requested" ||
                      (report.state as any) === "retest requested") && (
                      <>
                        <CustomActionButton
                          onClick={() =>
                            showConfirmation(
                              REPORT_STATUS_INFO.REQUEST_RETEST.title,
                              REPORT_STATUS_INFO.REQUEST_RETEST.description,
                              handleRetestRequest,
                              REPORT_STATUS_INFO.REQUEST_RETEST.info
                            )
                          }
                          disabled={currentRetestStatus !== "Request Retest"}
                        >
                          {currentRetestStatus}
                        </CustomActionButton>
                        {currentRetestStatus === "Retest Requested" && (
                          <CustomActionButton
                            onClick={() =>
                              showConfirmation(
                                REPORT_STATUS_INFO.DELETE_RETEST.title,
                                REPORT_STATUS_INFO.DELETE_RETEST.description,
                                handleDeleteRetestRequest,
                                REPORT_STATUS_INFO.DELETE_RETEST.info
                              )
                            }
                          >
                            Delete Retest Request
                          </CustomActionButton>
                        )}
                      </>
                    )}

                  {(role === UserRole.BUSINESS ||
                    role === UserRole.DEVELOPER ||
                    role === UserRole.BUSINESS_MANAGER ||
                    role === UserRole.BUSINESS_ADMINISTRATOR) &&
                    report.state === ReportState.BUSINESS_APPROVED && (
                      <>
                        <CustomActionButton
                          onClick={() =>
                            showConfirmation(
                              REPORT_STATUS_INFO.REQUEST_FIX.title,
                              REPORT_STATUS_INFO.REQUEST_FIX.description,
                              () => updateReportState("requestFix"),
                              REPORT_STATUS_INFO.REQUEST_FIX.info
                            )
                          }
                        >
                          Fixing the Vulnerability
                        </CustomActionButton>
                      </>
                    )}

                  {report.attachments && (
                    <CustomActionButton
                      onClick={() => window.open(report.attachments)}
                      className="gap-2"
                    >
                      <DownloadIcon className="fill-ctb-blue-400" /> Download
                      Attachments
                    </CustomActionButton>
                  )}

                  {(role === UserRole.BUSINESS ||
                    role === UserRole.DEVELOPER ||
                    role === UserRole.BUSINESS_MANAGER ||
                    role === UserRole.BUSINESS_ADMINISTRATOR) &&
                    report.state === ReportState.QA_MODIFIED && (
                      <>
                        <CustomActionButton
                          onClick={() =>
                            showConfirmation(
                              REPORT_STATUS_INFO.APPROVE.title,
                              REPORT_STATUS_INFO.APPROVE.description,
                              () => updateReportState("approved"),
                              REPORT_STATUS_INFO.APPROVE.info
                            )
                          }
                        >
                          Approve
                        </CustomActionButton>

                        <CustomActionButton
                          onClick={() =>
                            showConfirmation(
                              REPORT_STATUS_INFO.ACCEPT_RISK.title,
                              REPORT_STATUS_INFO.ACCEPT_RISK.description,
                              () => updateReportState("acceptRisk"),
                              REPORT_STATUS_INFO.ACCEPT_RISK.info
                            )
                          }
                        >
                          Accept risk
                        </CustomActionButton>

                        <CustomActionButton
                          onClick={() =>
                            showConfirmation(
                              REPORT_STATUS_INFO.NON_ACTIONABLE.title,
                              REPORT_STATUS_INFO.NON_ACTIONABLE.description,
                              () => updateReportState("nonActionableIssue"),
                              REPORT_STATUS_INFO.NON_ACTIONABLE.info
                            )
                          }
                        >
                          Non-Actionable Issue
                        </CustomActionButton>
                      </>
                    )}

                  {(role === UserRole.BUSINESS ||
                    role === UserRole.DEVELOPER ||
                    role === UserRole.BUSINESS_MANAGER ||
                    role === UserRole.BUSINESS_ADMINISTRATOR) &&
                    report.state ===
                    ReportState.BUSINESS_NON_ACTIONABLE_ISSUE && (
                      <>
                        <CustomActionButton
                          onClick={() =>
                            showConfirmation(
                              REPORT_STATUS_INFO.REQUEST_INFO.title,
                              REPORT_STATUS_INFO.REQUEST_INFO.description,
                              () => updateReportState("request info"),
                              REPORT_STATUS_INFO.REQUEST_INFO.info
                            )
                          }
                        >
                          Request more info
                        </CustomActionButton>
                      </>
                    )}

                  {(role === UserRole.BUSINESS ||
                    role === UserRole.DEVELOPER ||
                    role === UserRole.BUSINESS_MANAGER ||
                    role === UserRole.BUSINESS_ADMINISTRATOR) &&
                    (report.state === ReportState.TRIAGE_APPROVED ||
                      report.state === ReportState.BUSINESS_REVIEW) && (
                      <>
                        <CustomActionButton
                          onClick={() =>
                            showConfirmation(
                              REPORT_STATUS_INFO.APPROVE.title,
                              REPORT_STATUS_INFO.APPROVE.description,
                              () => updateReportState("approved"),
                              REPORT_STATUS_INFO.APPROVE.info
                            )
                          }
                        >
                          Approve
                        </CustomActionButton>
                        <CustomActionButton
                          onClick={() =>
                            showConfirmation(
                              REPORT_STATUS_INFO.REQUEST_INFO.title,
                              REPORT_STATUS_INFO.REQUEST_INFO.description,
                              () => updateReportState("request info"),
                              REPORT_STATUS_INFO.REQUEST_INFO.info
                            )
                          }
                        >
                          Request more info
                        </CustomActionButton>

                        <CustomActionButton
                          onClick={() =>
                            showConfirmation(
                              REPORT_STATUS_INFO.ACCEPT_RISK.title,
                              REPORT_STATUS_INFO.ACCEPT_RISK.description,
                              () => updateReportState("acceptRisk"),
                              REPORT_STATUS_INFO.ACCEPT_RISK.info
                            )
                          }
                        >
                          Accept risk
                        </CustomActionButton>

                        <CustomActionButton
                          onClick={() =>
                            showConfirmation(
                              REPORT_STATUS_INFO.NON_ACTIONABLE.title,
                              REPORT_STATUS_INFO.NON_ACTIONABLE.description,
                              () => updateReportState("nonActionableIssue"),
                              REPORT_STATUS_INFO.NON_ACTIONABLE.info
                            )
                          }
                        >
                          Non-Actionable Issue
                        </CustomActionButton>
                      </>
                    )}

                  {(role === UserRole.BUSINESS ||
                    role === UserRole.BUSINESS_ADMINISTRATOR ||
                    role === UserRole.BUSINESS_MANAGER ||
                    role === UserRole.DEVELOPER) &&
                    report.state === ReportState.BUSINESS_REQUEST_INFO && (
                      <>
                        <CustomActionButton
                          onClick={() =>
                            showConfirmation(
                              REPORT_STATUS_INFO.APPROVE.title,
                              REPORT_STATUS_INFO.APPROVE.description,
                              () => updateReportState("approved"),
                              REPORT_STATUS_INFO.APPROVE.info
                            )
                          }
                        >
                          Approve
                        </CustomActionButton>

                        <CustomActionButton
                          onClick={() =>
                            showConfirmation(
                              REPORT_STATUS_INFO.ACCEPT_RISK.title,
                              REPORT_STATUS_INFO.ACCEPT_RISK.description,
                              () => updateReportState("acceptRisk"),
                              REPORT_STATUS_INFO.ACCEPT_RISK.info
                            )
                          }
                        >
                          Accept risk
                        </CustomActionButton>

                        <CustomActionButton
                          onClick={() =>
                            showConfirmation(
                              REPORT_STATUS_INFO.NON_ACTIONABLE.title,
                              REPORT_STATUS_INFO.NON_ACTIONABLE.description,
                              () => updateReportState("nonActionableIssue"),
                              REPORT_STATUS_INFO.NON_ACTIONABLE.info
                            )
                          }
                        >
                          Non-Actionable Issue
                        </CustomActionButton>
                      </>
                    )}

                  {(role === UserRole.ADMIN ||
                    role === UserRole.SUB_ADMIN ||
                    role === UserRole.ADMIN_MANAGER ||
                    role === UserRole.QA) &&
                    report.state === ReportState.BUSINESS_REQUEST_INFO && (
                      <>
                        <CustomActionButton
                          onClick={() =>
                            showConfirmation(
                              REPORT_STATUS_INFO.MODIFIED.title,
                              REPORT_STATUS_INFO.MODIFIED.description,
                              () => updateReportState("QA Modified"),
                              REPORT_STATUS_INFO.MODIFIED.info
                            )
                          }
                        >
                          Modified
                        </CustomActionButton>
                        <CustomActionButton
                          onClick={() =>
                            showConfirmation(
                              REPORT_STATUS_INFO.OUT_OF_SCOPE.title,
                              REPORT_STATUS_INFO.OUT_OF_SCOPE.description,
                              () => updateReportState("out of scope"),
                              REPORT_STATUS_INFO.OUT_OF_SCOPE.info
                            )
                          }
                        >
                          Out of Scope
                        </CustomActionButton>
                        <CustomActionButton
                          onClick={() =>
                            showConfirmation(
                              REPORT_STATUS_INFO.NON_ACTIONABLE.title,
                              REPORT_STATUS_INFO.NON_ACTIONABLE.description,
                              () => updateReportState("nonActionableIssue"),
                              REPORT_STATUS_INFO.NON_ACTIONABLE.info
                            )
                          }
                        >
                          Non-Actionable Issue
                        </CustomActionButton>
                      </>
                    )}
                </div>
                {role === UserRole.RESEARCHER &&
                  (report.status === ReportStatus.DRAFT ||
                    report.state === ReportState.TRIAGE_REJECTED ||
                    report.state === ReportState.BUSINESS_REJECTED) && (
                    <CustomActionButton
                      onClick={() =>
                        showConfirmation(
                          REPORT_STATUS_INFO.SUBMIT.title,
                          REPORT_STATUS_INFO.SUBMIT.description,
                          handleSubmitReport,
                          REPORT_STATUS_INFO.SUBMIT.info
                        )
                      }
                      className="mb-[20px] ml-[45px] w-[200px]"
                      disabled={
                        isSubmitting ||
                        !(
                          report.title &&
                          report.programId &&
                          report.scope &&
                          report.severity &&
                          report.category &&
                          report.description &&
                          report.instructions &&
                          report.impact &&
                          report.fix
                        )
                      }
                    >
                      {isSubmitting ? "Submitting..." : "Submit"}
                    </CustomActionButton>
                  )}

                {/* Primary Action Buttons */}
                <div className="mt-6 flex flex-col gap-3">

                  <a
                    href="https://capturethebug-my.sharepoint.com/:w:/g/personal/rajeev_capturethebug_xyz/ETBDmRHP4CBItq00NxDADTYBRPNoAKqoEtp-ZPC0e4SWwA?e=XI7hSE"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full"
                  >
                    <OutlineButton className="w-full rounded-lg border-[1px] px-5 py-3 text-sm transition bg-white text-[#010D2C] border-[#58CC02] hover:bg-[#58CC02] hover:text-white hover:border-[#58CC02]">
                      View The Documentation
                    </OutlineButton>
                  </a>
                </div>
              </div>

              {/* Container 4: Bottom Right - Related Reports */}
              <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-md">
                <h3 className="mb-3 text-base font-semibold text-blue-700">
                  Sequential Reports
                </h3>
                {isLoadingRecommendations ? (
                  <div className="text-sm text-gray-400">Loading...</div>
                ) : recommendations.length === 0 ? (
                  <div className="text-sm text-gray-400">
                    No recommendations found.
                  </div>
                ) : (
                  <ul className="flex flex-col">
                    {recommendations.map((r, index) => (
                      <li
                        key={r.report_id}
                        className={`group flex cursor-pointer flex-col gap-2 px-3 py-3 transition hover:bg-blue-50 ${index < recommendations.length - 1 ? 'border-b' : ''
                          }`}
                        style={{
                          borderBottomColor: index < recommendations.length - 1 ? '#E5E7EB' : 'transparent',
                          borderBottomWidth: index < recommendations.length - 1 ? '1px' : '0px',
                          marginLeft: index < recommendations.length - 1 ? '-24px' : '0px',
                          marginRight: index < recommendations.length - 1 ? '-24px' : '0px',
                          paddingLeft: index < recommendations.length - 1 ? '27px' : '12px',
                          paddingRight: index < recommendations.length - 1 ? '27px' : '12px'
                        }}
                        onClick={() =>
                          navigate(`/dashboard/reports/${r.report_id}`)
                        }
                      >
                        <div className="flex items-start gap-2">
                          <div className="w-2 h-2 rounded-full bg-blue-600 mt-1.5 flex-shrink-0"></div>
                          <div className="truncate text-sm font-medium text-gray-900 group-hover:text-blue-700">
                            {r.report_title || "Untitled Report"}
                          </div>
                        </div>
                        <div className="flex flex-col gap-2 ml-4">
                          <div className="flex items-center gap-2">
                            {/* Severity Badge */}
                            <div
                              className="w-14 h-2 rounded-sm"
                              style={{
                                backgroundColor: r.severity_category === "CRITICAL" ? "#DC2626B2" :
                                  r.severity_category === "HIGH" ? "#F0813099" :
                                    r.severity_category === "MEDIUM" ? "#FFD15199" :
                                      r.severity_category === "LOW" ? "#DCFCE7" : "#DCFCE7"
                              }}
                            ></div>
                            <div className="text-xs font-medium capitalize text-gray-600">
                              {r.severity_category
                                ? r.severity_category.toLowerCase() + " severity"
                                : ""}
                            </div>
                          </div>
                          <div className="text-xs text-gray-500">
                            {r.submitted_date ? new Date(r.submitted_date).toLocaleDateString('en-US', {
                              month: 'short',
                              day: '2-digit',
                              year: 'numeric'
                            }) : "N/A"}
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </section>

            {/* Add the ConfirmationModal component */}
            <ConfirmationModal
              isOpen={modalConfig.isOpen}
              onClose={closeModal}
              onConfirm={modalConfig.onConfirm}
              title={modalConfig.title}
              description={modalConfig.description}
              info={modalConfig.info}
            />
          </>
        )}
      </DetailViewLayout>
    </section>
  );
};

export default ReportPage;
